import React, { useState, useEffect } from "react";
import PatientDetails from "./patientDetails.js";
import { FaRegHandshake } from "react-icons/fa";
import axios from "axios";
import Select from "react-select";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2"; // Fixed: Removed extra space

const AccountDetails = ({ heading }) => {
  // State for account selection and data
  const [selectedAccount, setSelectedAccount] = useState("");     // Selected account ID
  const [accounts, setAccounts] = useState([]);                   // List of available accounts
  const [loading, setLoading] = useState(true);                   // Loading state for accounts
  const [error, setError] = useState(null);                       // Error message if fetch fails
  const [formData, setFormData] = useState(null);                 // Data from PatientDetails child
  const [initialCaseData, setInitialCaseData] = useState(null);   // Case data if editing existing case
  const [receivingData, setReceivingData] = useState(true);       // Flag to allow data from child
  const [formErrors, setFormErrors] = useState({                  // Validation errors
    account: "",
    provider: "",
    lastName: "",
    procedures: "",
    delivery_date: "",
  });

  const { caseId } = useParams(); // For editing existing case via URL

  // Function to retrieve userLabId from localStorage (multiple fallback checks)
  const getUserLabId = () => {
    try {
      const directLabId = localStorage.getItem('userLabId');
      if (directLabId) return directLabId;

      const possibleKeys = ['user', 'userData', 'auth', 'currentUser', 'labUser', 'labUserData'];

      for (const key of possibleKeys) {
        const storedData = localStorage.getItem(key);
        if (storedData) {
          try {
            const parsed = JSON.parse(storedData);
            const userLabId =
              parsed.userLabId ||
              parsed.labId ||
              parsed.id ||
              (parsed.data && parsed.data.userLabId) ||
              (parsed.data && parsed.data.labId) ||
              (parsed.user && parsed.user.userLabId) ||
              (parsed.user && parsed.user.labId);

            if (userLabId) return userLabId;

            if (typeof storedData === 'string' && storedData.match(/^000000\d{2}$/)) {
              return storedData;
            }
          } catch (e) {
            continue;
          }
        }
      }

      // Scan all localStorage for raw labId
      for (let i = 0; i < localStorage.length; i++) {
        const value = localStorage.getItem(localStorage.key(i));
        if (value && value.match(/^000000\d{2}$/)) {
          return value;
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  // Get user type (lab or admin) from storage
  const getUserType = () => {
    const userType = localStorage.getItem('userType');
    if (!userType) {
      const labId = getUserLabId();
      return labId ? 'lab' : 'admin';
    }
    return userType;
  };

  // Handle account selection change
  const handleAccountChange = (event) => {
    const value = event.target.value;
    setSelectedAccount(value);
    setFormErrors((prev) => ({ ...prev, account: "" }));
  };

  // Receive data from PatientDetails component
  const handleDataChange = (data) => {
    if (receivingData) {
      setFormData(data);
      setFormErrors((prev) => ({
        ...prev,
        provider: data?.selectedProvider?.value ? "" : prev.provider,
        lastName: data?.last_name ? "" : prev.lastName,
        procedures:
          data?.workRequested?.procedures &&
          data?.workRequested.procedures.length > 0 &&
          data?.workRequested.procedures.some((p) => p.procedure)
            ? ""
            : prev.procedures,
      }));
    }
  };

  // Validate form before submission
  const validateForm = () => {
    let valid = true;
    const newErrors = {
      account: "",
      provider: "",
      lastName: "",
      procedures: "",
      delivery_date: "",
    };

    if (!selectedAccount) {
      newErrors.account = "Account is required";
      valid = false;
    }
    if (!formData?.selectedProvider?.value) {
      newErrors.provider = "Provider is required";
      valid = false;
    }
    if (!formData?.last_name) {
      newErrors.lastName = "Last name is required";
      valid = false;
    }
    if (
      !formData?.workRequested?.procedures ||
      formData.workRequested.procedures.length === 0 ||
      formData.workRequested.procedures.every((p) => !p.procedure)
    ) {
      newErrors.procedures = "At least one procedure is required";
      valid = false;
    }
    if (!formData?.workRequested?.delivery_date) {
      newErrors.delivery_date = "Delivery date is required";
      valid = false;
    }

    setFormErrors(newErrors);
    return valid;
  };

  // Fetch accounts on component mount (lab-specific or all)
  useEffect(() => {
    const fetchData = async () => {
      const userLabId = getUserLabId();
      const userType = getUserType();

      // Check if userLabId is required but not found
      if (userType === 'lab' && !userLabId) {
        setLoading(false);
        Swal.fire({
          icon: "warning",
          title: "Lab Not Selected",
          text: "Please select a lab first to create a case.",
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: true,
          confirmButtonColor: "#004aad"
        });
        setError("No lab selected. Please select a lab first.");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        let fetchedAccounts = [];

        if (userType === 'lab' && userLabId) {
          // Lab user: fetch only their accounts
          const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${userLabId}`);
          if (response.data.success && response.data.data) {
            fetchedAccounts = response.data.data;
          } else {
            setError("No accounts found for your lab.");
          }
        } else {
          // Admin: fetch all accounts
          const response = await axios.get("https://hms.backend.hiragroupcloud.com/api/account/account");
          if (response.data.success && response.data.data) {
            fetchedAccounts = response.data.data;
          } else {
            setError("No accounts found.");
          }
        }

        // Normalize and sort accounts alphabetically
        const normalizedAccounts = fetchedAccounts
          .filter((account) => account && (account.account_name || account.name))
          .map((account) => ({
            accountId: account.accountId || account._id || "N/A",
            account_name: account.account_name || account.name || "Unnamed Account",
            address: account.address || "",
            city: account.city || "",
            state: account.state || "",
            country: account.country || "",
            addedBy: account.addedBy || "Unknown",
            source: account.source || "HMS"
          }));

        const sortedAccounts = normalizedAccounts.sort((a, b) =>
          a.account_name.toLowerCase().localeCompare(b.account_name.toLowerCase())
        );

        setAccounts(sortedAccounts);

        // REMOVED: Auto-select if only one account
        // Instead, keep selectedAccount as empty string to show "Select Account" placeholder
        setSelectedAccount("");

        setLoading(false);
      } catch (error) {
        let errorMessage = "Error fetching accounts.";
        if (error.response) {
          errorMessage = `Server error: ${error.response.status}`;
        } else if (error.request) {
          errorMessage = "No response from server.";
        }

        setError(errorMessage);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Prefill form if caseId is in URL (editing existing case)
  useEffect(() => {
    const fetchCaseData = async () => {
      if (caseId && accounts.length > 0) {
        try {
          const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId}`);
          const caseData = response.data;
          setInitialCaseData(caseData);

          // Try to match and pre-select account
          const accountMatch = accounts.find(
            (acc) =>
              acc.accountId === caseData.account_holder ||
              acc.account_name?.toLowerCase() === caseData.account_holder?.toLowerCase()
          );
          if (accountMatch) {
            setSelectedAccount(accountMatch.accountId);
          }
        } catch (error) {
          // Silent fail on prefill
        }
      }
    };

    fetchCaseData();
  }, [caseId, accounts]);

  // Submit the new case
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setReceivingData(false);

    const userLabId = getUserLabId();
    const userType = getUserType();

    if (userType === 'lab' && !userLabId) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "User Lab ID not found. Please login again.",
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
        confirmButtonColor: "#004aad"
      });
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("account_holder", selectedAccount);
    formDataToSend.append("first_name", formData.first_name || "");
    formDataToSend.append("last_name", formData.last_name || "");

    if (userLabId) formDataToSend.append("userLabId", userLabId);
    formDataToSend.append("userType", userType);

    // Birth date
    const birthDate = formData.birth_date;
    if (birthDate) {
      const date = birthDate instanceof Date ? birthDate : new Date(birthDate);
      formDataToSend.append("birth_date", isNaN(date) ? "" : date.toISOString());
    }

    // Notes
    formDataToSend.append("subject", formData.notes.subject || "");
    formDataToSend.append("recipient", formData.notes.recipient || "");
    formDataToSend.append("tags", JSON.stringify(formData.notes.tags || []));
    formDataToSend.append("try_in", formData.notes.buttonClickCount?.tryIn || false);
    formDataToSend.append("emerg", formData.notes.buttonClickCount?.emerg || false);
    formDataToSend.append("next_case", formData.notes.buttonClickCount?.nextCase || false);
    formDataToSend.append("pickup", formData.notes.buttonClickCount?.pickup || false);
    formDataToSend.append("editorHtml", formData.notes.editorHtml || "");

    // Attached files
    if (formData.notes.attachedFiles?.length > 0) {
      formData.notes.attachedFiles.forEach((file) => {
        formDataToSend.append("attached_files", file);
      });
    }

    // Provider & gender
    formDataToSend.append("gender", formData.selectedGender?.value || "");
    formDataToSend.append("provider_name", formData.selectedProvider?.value || "");

    // Dates
    const appendDate = (key, dateValue) => {
      if (dateValue) {
        const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
        formDataToSend.append(key, isNaN(date) ? "" : date.toISOString());
      } else {
        formDataToSend.append(key, "");
      }
    };

    appendDate("start_date", formData.workRequested.start_date);
    appendDate("arrive_date", formData.workRequested.arrive_date);
    appendDate("delivery_date", formData.workRequested.delivery_date);

    // Color & pan number
    formDataToSend.append("color", formData.workRequested.color?.name || "");
    formDataToSend.append("pan_number", formData.workRequested.pan_number || "");
    formDataToSend.append("procedures", JSON.stringify(formData.workRequested.procedures || []));

    if (formData.workRequested.components) {
      formDataToSend.append("components", JSON.stringify(formData.workRequested.components));
    }

    try {
      const response = await axios.post(
        "https://hms.backend.hiragroupcloud.com/api/cases/case",
        formDataToSend,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const caseNumber = response.data.caseNumber;
      if (caseNumber) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Case created successfully with Case ID: #${caseNumber}`,
          timer: 2000,                    // Auto close after 2 seconds
          timerProgressBar: true,
          showConfirmButton: false,       // No OK button
          confirmButtonColor: "#004aad"
        }).then(() => {
          window.location.href = `/case/${caseNumber}`;
        });

        // Fallback navigation after 2 seconds (in case Swal doesn't trigger then())
        setTimeout(() => {
          window.location.href = `/case/${caseNumber}`;
        }, 2000);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: "There was an error creating the case. Please try again.",
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
        confirmButtonColor: "#004aad"
      });
    }
  };

  return (
    <>
      <div style={{ margin: "0 auto", padding: "0px" }}>
        <h2 className="fancy-heading" style={{ marginTop: "10px" }}>
          {heading}
        </h2>
        <h3 className="title" style={{ backgroundColor: "white", color: "#004aad" }}>
          <FaRegHandshake style={{ marginRight: "5px" }} />
          Account Details
        </h3>

        {/* Error message */}
        {error && (
          <div style={{
            backgroundColor: "#ffebee",
            color: "#c62828",
            padding: "10px",
            marginBottom: "15px",
            borderRadius: "4px",
            border: "1px solid #ffcdd2"
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        <div style={{
          border: "1px solid #ccc",
          padding: "20px",
          borderTop: "3px solid #004aad",
          backgroundColor: "#f0f0f0",
          width: "100%",
          margin: 0,
        }}>
          <div className="row">
            <div className="col-3" style={{ display: "inline-block", marginRight: "20px" }}>
              <label htmlFor="account" className="form-label" style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                Account <span style={{ color: "red" }}>*</span>
              </label>

              <Select
                id="account"
                name="account"
                options={accounts.map((account) => ({
                  value: account.accountId,
                  label: account.account_name,
                }))}
                value={
                  selectedAccount
                    ? {
                        value: selectedAccount,
                        label: accounts.find((a) => a.accountId === selectedAccount)?.account_name || "",
                      }
                    : null
                }
                onChange={(selected) => {
                  setSelectedAccount(selected?.value || "");
                  setFormErrors((prev) => ({ ...prev, account: "" }));
                }}
                isSearchable
                placeholder={loading ? "Loading accounts..." : "Select Account"}
                classNamePrefix="react-select"
                isLoading={loading}
                isDisabled={loading}
                noOptionsMessage={() => "No accounts available"}
              />

              {formErrors.account && (
                <div style={{ color: "red", fontSize: "12px" }}>
                  {formErrors.account}
                </div>
              )}

              {!loading && accounts.length > 0 && (
                <div style={{ fontSize: "11px", color: "#666", marginTop: "5px" }}>
                  {accounts.length} account(s) available - Please select an account
                </div>
              )}

              {!loading && accounts.length === 0 && !error && (
                <div style={{ fontSize: "11px", color: "#ff9800", marginTop: "5px" }}>
                  Select the Lab for booking a Case
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Patient Details Form (only when account selected) */}
        {selectedAccount && (
          <PatientDetails
            account={selectedAccount}
            onDataChange={handleDataChange}
            formErrors={formErrors}
            initialCaseData={initialCaseData}
          />
        )}

        {/* Submit Button */}
        <div style={{ marginTop: "10px" }}>
          <button
            onClick={handleSubmit}
            style={{
              backgroundColor: "#004aad",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              padding: "10px 20px",
              cursor: "pointer",
              fontSize: "16px",
            }}
            disabled={!selectedAccount}
          >
            Submit Case
          </button>
        </div>
      </div>
    </>
  );
};

export default AccountDetails;